import React, {useEffect} from 'react';

import {browserHistory, withRouter, WithRouterProps} from 'react-router';

import {Filterbar} from 'app/components/general/DashboardPage/Filterbar';
import {
  applyFilter,
  getFilterLink,
} from 'app/general/dashboards/DashboardFilterbar';
import {DashboardFilterbarSort} from 'app/general/dashboards/DashboardFilterbarSort';
import {ActionsButton} from 'app/general/widgets/ActionsButton';
import {deleteSignoffTemplate} from 'app/signoffs/actions';
import {SORT_BY_TYPES} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';

import {AddSignoffRulesButton} from './AddSignoffRulesButton';

import {TeamPageFilterBarStructure} from '../components/TeamPageFilterBarStructure';

import type {TeamSerializedType} from 'sweet-types';

interface Props extends WithRouterProps<unknown> {
  team: TeamSerializedType;
  requestDataRefetch: () => any;
  viewingAll: boolean;
  setViewingAll: (arg0: boolean) => void;
  showManageRulesModal: () => void;
}

export const SignoffListOptionsBar = withRouter<Props>(
  ({
    team,
    requestDataRefetch,
    viewingAll,
    setViewingAll,
    router,
    location,
    showManageRulesModal,
  }: Props) => {
    const actions = useAsyncActions({
      deleteSignoffTemplate,
    });

    const {groupby, ordering} = location.query;

    const setFilter = (filter: {[field: string]: string | null}) =>
      applyFilter(router, location, filter);

    const deleteSignoffRules = (router) =>
      actions.deleteSignoffTemplate(team.signofftemplate).then(() => {
        router.replace({...router.location, query: {}});
        return requestDataRefetch();
      });

    const resetFilters = () => {
      router.replace({
        pathname: location.pathname,
      });
      const link = getFilterLink(
        browserHistory,
        browserHistory.getCurrentLocation(),
        {}
      );
      browserHistory.push(link);
    };

    useEffect(() => {
      if (location.query.search || location.query.tag) {
        setViewingAll(false);
      } else if (!location.query.search && !location.query.tag) {
        setViewingAll(true);
      }
    }, [location.query]);

    return (
      <div className="mt-2 mt-sm-0">
        <TeamPageFilterBarStructure>
          {team.signofftemplate != null ? (
            <Filterbar
              ct="document"
              show={{
                search: true,
                tags: true,
                team: false,
                filters: false,
                sort: false,
              }}
              resetFilters={resetFilters}
              viewingAll={viewingAll}
              className="col-12 col-lg mb-2 mb-lg-0 mr-lg-2"
              borderless={true}
              searchFieldClass="col-12 col-sm-6 col-xl-3 pr-sm-2 mb-2 mb-sm-2 mb-xl-0"
              tagsFieldClass="col-12 col-sm-6 col-xl pr-md-0 pr-xl-2 mb-2 mb-sm-2 mb-xl-0"
            >
              <div className="col-12 col-sm-6 col-lg-6 col-xl-3 pr-sm-2 mb-2 mb-sm-0">
                <select
                  className="form-control"
                  value={groupby}
                  onChange={(e) =>
                    setFilter({groupby: e.target.value as 'user' | 'doc'})
                  }
                >
                  <option value="user">{gt('Group by Member')}</option>
                  <option value="doc">{gt('Group by Document')}</option>
                </select>
              </div>
              <div className="col-12 col-sm col-xl-3">
                <DashboardFilterbarSort
                  value={ordering}
                  onChange={(ordering: string) => setFilter({ordering})}
                  options={[
                    SORT_BY_TYPES.ALPHABETHICALLY,
                    SORT_BY_TYPES.LAST_REQUEST_SENT_DATE_DESC,
                    SORT_BY_TYPES.LAST_SIGNED_OFF_DATE_DESC,
                  ].concat(location.query.search ? [SORT_BY_TYPES.RANK] : [])}
                  backendDefaultSort={SORT_BY_TYPES.ALPHABETHICALLY}
                />
              </div>
            </Filterbar>
          ) : null}
          {team.can_edit ? (
            <div className="ml-auto col-auto align-items-center d-flex">
              {team.signofftemplate == null ? (
                <AddSignoffRulesButton showModal={showManageRulesModal} />
              ) : (
                <ActionsButton
                  actions={[
                    {
                      icon: 'fa fa-pen',
                      key: 'edit',
                      text: gt('Edit'),
                      callback: showManageRulesModal,
                    },
                    {
                      icon: 'fa fa-times',
                      key: 'remove',
                      text: gt('Remove'),
                      callback: () => deleteSignoffRules(router),
                    },
                  ]}
                >
                  {({disabled}) => (
                    <button className="btn btn-light" disabled={disabled}>
                      {gt('Signoff Rules')}{' '}
                      <i className="ml-1 fa fa-angle-down"></i>
                    </button>
                  )}
                </ActionsButton>
              )}
            </div>
          ) : null}
        </TeamPageFilterBarStructure>
      </div>
    );
  }
);
